import { Button, Col, Modal, Row } from "react-bootstrap";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { ReactSelect, SpinnerLoader } from "@services/ui-components";
import {
    useUpdateStorageProductHistoryMutation,
    useUpdateStorageProductRelationMutation,
} from "@me-team/host/main/warehouses_goods/graphql/warehouses_goods.hooks";
import React, { useEffect, useRef, useState } from "react";
import { Option } from "../../Products/types";
import * as Yup from "yup";
import QuantityInput from "../../AddNewProductList/AddProductListModal/QuantityInput";
import {
    CompanyAccess,
    StorageProductRelationActionType,
    StorageProductRelationHistoryAction,
    UpdateStorageProductRelationInput,
} from "@me-team/host/main/graphql/types";
import ModalProductInfo from "../components/ModalProductInfo";
import moment from "moment";
import { WearhousesModalProps } from "@me-team/common/src/components/Warehouse/modals/modalsUtils";
import { useGetSuppliersQuery } from "@me-team/host/main/warehouses/graphql/warehouses.hooks";
import CustomCreatableReactSelect from "@services/ui-components/src/select/CustomCreatableReactSelect";
import { useReactiveVar } from "@apollo/client";
import { currentBranchEmployeeIdVar, currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { useParams } from "react-router-dom";
import {
    isWarehouseTutorialActive,
    moveStepInLocalStorage,
    StepDirection,
} from "../../Joyride/WarehouseTutorial/joyrideStepHelpers";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

const AddGoodsWarehouseModal: React.FC<WearhousesModalProps> = ({
    show,
    handleModal,
    refetch,
    chosenProduct,
    toggleShowToast,
    isEditMode = false,
    centered = true,
    dialogClassName,
    dataTour,
    canEditOperation,
}) => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);
    const canCreateArrival = useRoleAccess(CompanyAccess.WarehouseProductArrival);
    const canSubmit = isEditMode ? Boolean(canEditOperation) : canCreateArrival;
    const [unitOfMeasurements, setUnitOfMeasurements] = useState([]);
    const tutorialActive = isWarehouseTutorialActive();
    const contentRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (contentRef.current && dataTour) {
            contentRef.current.setAttribute("data-tour", dataTour);
        }
    }, [dataTour, show]);

    const initialValues: UpdateStorageProductRelationInput = {
        quantity: isEditMode ? (chosenProduct?.operationQuantity ?? null) : null,
        unit: isEditMode && chosenProduct?.operationUnit?.id ? chosenProduct.operationUnit.id : null,
        comment: isEditMode && chosenProduct?.comment ? chosenProduct.comment : "",
        action: StorageProductRelationActionType.Arrival,
        supplier: isEditMode && chosenProduct?.supplier?.name ? chosenProduct.supplier.name : null,
    };
    const [updateStorageProductRelation] = useUpdateStorageProductRelationMutation({
        context: {
            errorType: "local",
        },
    });
    const [updateStorageProductHistory] = useUpdateStorageProductHistoryMutation({
        context: {
            errorType: "local",
        },
    });

    const { data: branchProductSuppliersData, refetch: suppliersRefetch } = useGetSuppliersQuery({
        variables: {
            companyId: +currentCompanyId,
            branchId: Number(branchId),
        },
        skip: !currentCompanyId || !branchId,
    });

    const productSupplierOptions =
        branchProductSuppliersData?.suppliers.map((supplier) => {
            return {
                id: supplier.id,
                name: supplier.name,
            };
        }) || [];

    const handleSubmit = async (values: typeof initialValues) => {
        if (!canSubmit) return;

        try {
            if (isEditMode) {
                if (currentBranchEmployeeId == null) return;
                await updateStorageProductHistory({
                    variables: {
                        input: [
                            {
                                storageProductRelationHistoryId: +chosenProduct?.id,
                                updateStorageProductRelationHistoryInputType: {
                                    quantity: +values?.quantity,
                                    unit: values.unit,
                                    comment: values.comment,
                                    supplier: values.supplier,
                                },
                            },
                        ],
                        branchId: Number(branchId),
                        employeeId: Number(currentBranchEmployeeId),
                        operationType: StorageProductRelationHistoryAction.Arrival,
                    },
                });
                tutorialActive && moveStepInLocalStorage(StepDirection.NEXT);
                tutorialActive && window.dispatchEvent(new Event("warehouseStepChanged"));
            } else {
                await updateStorageProductRelation({
                    variables: {
                        storageProductRelationId: +chosenProduct?.id,
                        input: {
                            ...values,
                            quantity: +values?.quantity,
                            action: values.action,
                            comment: values.comment,
                            supplier: values.supplier,
                        },
                        branchId: Number(branchId),
                    },
                });
            }
            refetch();
            await suppliersRefetch();
            handleModal();
            toggleShowToast(t("Saved"));
        } catch (error) {
            console.error(error);
        }
    };
    useEffect(() => {
        if (chosenProduct?.product) {
            const optionsUnitOfMeasurements =
                chosenProduct?.product?.units?.map((unit) => ({
                    value: unit.id,
                    label: unit.name,
                })) || [];
            setUnitOfMeasurements(optionsUnitOfMeasurements);
        }
    }, [chosenProduct?.product]);
    const validationSchema = () =>
        Yup.object().shape({
            quantity: Yup.number().required(t("validation.required")),
            unit: Yup.number().required(t("validation.required")),
            comment: Yup.string().max(400, t("The maximum length of the Description field is 400 characters")),
        });

    const initialSupplier =
        isEditMode && initialValues?.supplier
            ? {
                  value: initialValues?.supplier,
                  label: initialValues?.supplier,
                  name: initialValues?.supplier,
              }
            : null;

    return (
        <Modal
            size="lg"
            show={show}
            onHide={handleModal}
            centered={centered}
            dialogClassName={dialogClassName ? dialogClassName : ""}
            onEntered={() => {
                tutorialActive && requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
            }}
        >
            <div ref={contentRef}>
                <Modal.Header className="border-0" closeButton>
                    {isEditMode ? (
                        <h5 className="m-0">{t("Edit product addition")}</h5>
                    ) : (
                        <h5 className="m-0">
                            {t("Add products")} {t("to the warehouse")}
                        </h5>
                    )}
                </Modal.Header>
                <Modal.Body>
                    <ModalProductInfo
                        date={chosenProduct?.date || moment().format("YYYY-MM-DD HH:mm:ss")}
                        productName={chosenProduct?.product?.name}
                        brand={chosenProduct?.product?.brand}
                        categoryName={chosenProduct?.product?.productCategoryNew.name}
                        sku={chosenProduct?.product?.sku}
                        barcode={chosenProduct?.product?.barcode}
                        unitGroupName={chosenProduct?.product?.unitGroup.name}
                        remnants={chosenProduct?.remnantsForRender || chosenProduct?.quantityForRender}
                    />
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        validateOnChange={false}
                        enableReinitialize
                    >
                        {({ values, errors, setFieldValue, isSubmitting, setFieldTouched }) => (
                            <Form>
                                <Row className={"mx-0 mt-3"}>
                                    <Col sm={3} className="pr-0 mb-2 mb-sm-0">
                                        <label
                                            htmlFor="addWeight-field"
                                            className="h-100 d-flex align-items-center mb-0 text-dark form-label required text-break"
                                            style={{ maxHeight: "49px" }}
                                        >
                                            <span className="text-dark fw-bold fs-7 required-label">
                                                {t("Amount to be added")}
                                            </span>
                                        </label>
                                    </Col>
                                    <Col sm={9}>
                                        <QuantityInput
                                            name="quantity"
                                            id="addsProduct-quantity-field"
                                            placeholder={t("Amount to be added")}
                                            maxLength={9}
                                            isAllowZero={isEditMode}
                                        />
                                        <ErrorMessage
                                            name="quantity"
                                            component="div"
                                            className="fs-7 m-0 p-1 text-danger"
                                        />
                                    </Col>
                                </Row>
                                <Row className="pt-3 mb-3 mx-0">
                                    <Col sm={3} className="pr-0 mb-2 mb-sm-0">
                                        <label
                                            htmlFor="storageType"
                                            className="h-100 d-flex align-items-center mb-0 text-dark form-label required text-break"
                                            style={{ maxHeight: "49px" }}
                                        >
                                            <span className="text-dark fw-bold fs-7 required-label">
                                                {t("Unit of measurements")}
                                            </span>
                                        </label>
                                    </Col>
                                    <Col sm={9}>
                                        <ReactSelect
                                            id="addProduct-unit-field"
                                            name="unit"
                                            placeholder={t("Select an unit")}
                                            options={unitOfMeasurements}
                                            onChange={(selectedOption: Option) => {
                                                setFieldValue("unit", selectedOption.value, true);
                                                setFieldTouched("unit", true, false);
                                            }}
                                            isSearchable={false}
                                            value={unitOfMeasurements?.find(
                                                (option: Option) => option.value === values.unit
                                            )}
                                        />
                                        <ErrorMessage
                                            name="unit"
                                            component="div"
                                            className="fs-7 m-0 p-1 text-danger"
                                        />
                                    </Col>
                                </Row>
                                <Row className="mb-3 mx-0">
                                    <Col sm={3} className="d-flex pr-0 mb-2 mb-sm-0">
                                        <label
                                            htmlFor="supplier"
                                            className="h-100 d-flex align-items-center mb-0 text-dark form-label text-break"
                                        >
                                            <span className="text-dark fw-bold fs-7">{t("Supplier")}</span>
                                        </label>
                                    </Col>
                                    <Col sm={9}>
                                        <CustomCreatableReactSelect
                                            optionsArray={productSupplierOptions}
                                            placeholder={t("Select supplier")}
                                            setField={setFieldValue}
                                            fieldName="supplier"
                                            value="name"
                                            isTooltip={false}
                                            name={"supplier"}
                                            id={"addProduct-supplier-field"}
                                            initialValue={initialSupplier}
                                            maxLength={100}
                                            createOptionLabel={t("Create a supplier")}
                                            noOptionsText={t("Start typing the supplier name")}
                                            createOnBlur={true}
                                        />
                                        <ErrorMessage
                                            name="supplier"
                                            component="div"
                                            className="fs-7 m-0 p-1 text-danger"
                                        />
                                    </Col>
                                </Row>
                                <Row className="mb-3 mx-0">
                                    <Col sm={3} className="pr-0 mb-2 mb-sm-0">
                                        <label
                                            htmlFor="comment"
                                            className="mb-0 pt-sm-3 text-dark fw-bold fs-7 form-label text-break line-height-140"
                                        >
                                            {t("Comment")}
                                        </label>
                                    </Col>
                                    <Col sm={9}>
                                        <Field
                                            as="textarea"
                                            name="comment"
                                            placeholder={t("For example, enter the invoice number")}
                                            className={`fs-7 form-control ${errors?.comment ? "is-invalid" : ""}`}
                                            maxLength={400}
                                        />
                                        <span
                                            className={`fs-7 ${values.comment.length > 400 ? "text-danger" : "text-grey"}`}
                                        >
                                            {values.comment.length}/400
                                        </span>
                                        <ErrorMessage name="comment" component="div" className="text-danger" />
                                    </Col>
                                </Row>

                                <Row className="d-flex justify-content-end pt-6">
                                    <Col xs={6} sm="auto" className="mb-sm-0 pe-1">
                                        <Button
                                            variant="outline-primary"
                                            className="fw-normal custom-btn rounded-1"
                                            onClick={() => {
                                                tutorialActive && moveStepInLocalStorage(StepDirection.PREV);
                                                handleModal();
                                            }}
                                        >
                                            {t("Cancel")}
                                        </Button>
                                    </Col>
                                    <Col xs={6} sm="auto" className="mb-sm-0 ps-1">
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            className="fw-bold custom-btn rounded-1"
                                            disabled={isSubmitting || !canSubmit}
                                        >
                                            {isSubmitting ? (
                                                <SpinnerLoader
                                                    variant="light"
                                                    loadingText={t(isEditMode ? "Saving..." : "Adding...")}
                                                />
                                            ) : (
                                                t(isEditMode ? "Save" : "Add")
                                            )}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        )}
                    </Formik>
                </Modal.Body>
            </div>
        </Modal>
    );
};

export default AddGoodsWarehouseModal;
